// <s-wishlist-list> renders the products saved in this browser's wishlist (localStorage) from each product's .js endpoint, with remove buttons and an empty state.
const STORAGE_KEY = 'muc-wishlist';
const CHANGE_EVENT = 'muc:wishlist-change';

const readList = () => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

class SWishlistList extends HTMLElement {
  #list;
  #empty;
  #cache = new Map();
  #renderId = 0;

  connectedCallback() {
    this.#list = this.querySelector('[data-wishlist-items]');
    this.#empty = this.querySelector('[data-wishlist-empty]');
    if (!this.#list) return;
    window.addEventListener(CHANGE_EVENT, this.#render);
    window.addEventListener('storage', this.#render);
    this.closest('dialog')?.addEventListener('toggle', this.#render);
    this.#render();
  }

  disconnectedCallback() {
    window.removeEventListener(CHANGE_EVENT, this.#render);
    window.removeEventListener('storage', this.#render);
  }

  async #fetchProduct(handle) {
    if (this.#cache.has(handle)) return this.#cache.get(handle);
    const root = this.dataset.root || '/';
    const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    this.#cache.set(handle, request);
    return request;
  }

  #remove(handle) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(readList().filter((item) => item !== handle)));
    } catch {
      return;
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  }

  #render = async () => {
    const renderId = ++this.#renderId;
    const handles = readList();
    const products = (await Promise.all(handles.map((handle) => this.#fetchProduct(handle)))).filter(Boolean);
    if (renderId !== this.#renderId) return;

    const money = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency',
      currency: this.dataset.currency || 'INR',
      maximumFractionDigits: 0,
    });

    this.#list.replaceChildren(
      ...products.map((product) => {
        const item = document.createElement('li');
        item.className = 'flex items-center gap-3 rounded-xl border border-[#D4AF37]/30 bg-white p-2';

        const link = document.createElement('a');
        link.href = product.url;
        link.className = 'flex min-w-0 flex-1 items-center gap-3 text-on-surface no-underline hover:text-accent';
        if (product.featured_image) {
          const image = document.createElement('img');
          image.src = `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=160`;
          image.alt = '';
          image.width = 64;
          image.height = 64;
          image.loading = 'lazy';
          image.className = 'size-16 shrink-0 rounded-lg bg-[#FAF6F0] object-cover';
          link.append(image);
        }
        const text = document.createElement('span');
        text.className = 'flex min-w-0 flex-col gap-1';
        const title = document.createElement('span');
        title.className = 'line-clamp-2 text-xs font-semibold';
        title.textContent = product.title;
        const price = document.createElement('span');
        price.className = 'text-sm font-bold';
        price.textContent = money.format(product.price / 100);
        text.append(title, price);
        link.append(text);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'inline-flex size-touch-target shrink-0 cursor-pointer items-center justify-center rounded-full text-on-surface/60 hover:bg-[#FAF6F0] hover:text-on-surface';
        remove.setAttribute('aria-label', `${this.dataset.removeLabel || 'Remove'}: ${product.title}`);
        remove.textContent = '✕';
        remove.addEventListener('click', () => this.#remove(product.handle));

        item.append(link, remove);
        return item;
      }),
    );
    if (this.#empty) this.#empty.hidden = products.length > 0;
  };
}

if (!customElements.get('s-wishlist-list')) customElements.define('s-wishlist-list', SWishlistList);
